import type { Ref } from 'vue';

import {
    BackendErrorCode,
    DirectSaleComputedState,
    type DirectSaleItemEntity,
    type DirectSaleListingItemEntity,
    DirectSaleState,
    NegotiationState,
} from '~/apiClient';
import type { InspectionExpiryStatus, SaleStatus } from '~/types/saleStatus';

interface UseDirectSaleStatusReturn extends SaleStatus {
    hasOrder: ComputedRef<boolean>;
    isSold: ComputedRef<boolean>;
}

type DirectSaleStatusTone = 'blue' | 'green' | 'neutral';

export default function useDirectSaleStatus(directSale: Ref<DirectSaleItemEntity | DirectSaleListingItemEntity>): UseDirectSaleStatusReturn {
    const { companyScopeIds } = useAuthUtils();
    const { tt } = useTypedI18n();
    const dayjs = useDayjs();

    const userMeta = computed(() => directSale.value.userMeta);
    const purchaseInfo = computed(() => directSale.value.purchaseInfo);

    const isSeller = computed(() => !!directSale.value && isSellerOfSale(directSale.value, companyScopeIds.value));
    const hasOrder = computed(
        () => !!userMeta.value?.pendingPayment || !!userMeta.value?.paid || !!purchaseInfo.value?.isPaid || !!purchaseInfo.value?.isPendingPayment
    );
    const isSold = computed(
        () =>
            !!directSale.value.computedState &&
            [DirectSaleComputedState.Sold, DirectSaleComputedState.PendingPayment].includes(directSale.value.computedState)
    );
    const hasNoActiveOrder = computed(() => directSale.value.computedState === DirectSaleComputedState.NoActiveOrder);
    const isNotPaid = computed(() => userMeta.value?.cantBuyReason === BackendErrorCode.CantBuyDirectSaleOrderNotPaid);
    const hasActiveNegotiation = computed(
        () => directSale.value.purchaseInfo?.hasOpenNegotiation || directSale.value.negotiations?.some(n => n.state === NegotiationState.Open)
    );

    const tone = computed<DirectSaleStatusTone>(() => {
        if (directSale.value.soldOffPlatform || isNotPaid.value) return 'neutral';

        if (isSold.value && (hasOrder.value || isSeller.value)) return 'green';

        if (hasNoActiveOrder.value) return 'blue';

        return 'neutral';
    });

    const statusSectionColor = computed(() => {
        const colors: Record<DirectSaleStatusTone, string> = {
            blue: 'bg-primary-50',
            green: 'bg-success-50',
            neutral: 'bg-neutral-100',
        } as const;

        return colors[tone.value];
    });

    const title = computed(() => {
        if (directSale.value.soldOffPlatform) return tt('sale.soldOffPlatform');

        if (directSale.value.state === DirectSaleState.ToBeApproved) return tt('sdp.DirectSale.directSaleStatus.sellerReservePrice');

        if (isSold.value) return hasOrder.value ? tt('sdp.DirectSale.directSaleStatus.purchased') : tt('sdp.DirectSale.directSaleStatus.sold');

        if (hasActiveNegotiation.value) return tt('sdp.DirectSale.directSaleStatus.inNegotiation');

        if (hasNoActiveOrder.value) return tt('sdp.DirectSale.directSaleStatus.available');

        return tt('sdp.DirectSale.directSaleStatus.notAvailable');
    });

    const titleColor = computed(() => {
        return 'text-default';
    });

    const titleAnnotation = computed(() => {
        if (isNotPaid.value) return tt('sdp.DirectSale.directSaleStatus.annotation.notPaid');

        if (hasOrder.value && directSale.value.computedState === DirectSaleComputedState.PendingPayment)
            return tt('sdp.DirectSale.directSaleStatus.annotation.pendingPayment');

        if (directSale.value && [DirectSaleState.ToBeScheduled, DirectSaleState.Waiting, DirectSaleState.Pending].includes(directSale.value.state))
            return tt('sdp.DirectSale.directSaleStatus.annotation.comingSoon');

        if (isSeller.value && directSale.value.computedState === DirectSaleComputedState.CloseUnsold && !directSale.value.soldOffPlatform)
            return tt('sdp.DirectSale.directSaleStatus.annotation.unsold');

        return;
    });

    const titleAnnotationColor = computed(() => {
        if (isNotPaid.value) return 'text-error-600';

        const colors: Record<DirectSaleStatusTone, string> = {
            blue: 'text-primary-600',
            green: 'text-success-600',
            neutral: 'text-default',
        } as const;

        return colors[tone.value];
    });

    const priceColor = computed(() => {
        const colors: Record<DirectSaleStatusTone, string> = {
            blue: 'text-primary-600',
            green: 'text-primary-600',
            neutral: 'text-default',
        } as const;

        return colors[tone.value];
    });

    const statusDotColor = computed(() => {
        const colors: Record<DirectSaleStatusTone, string> = {
            blue: 'bg-primary',
            green: 'bg-success-500',
            neutral: 'bg-neutral-500',
        } as const;

        return colors[tone.value];
    });

    const isFeeNoticeHidden = computed(
        () =>
            !!directSale.value &&
            ![DirectSaleState.Pending, DirectSaleState.Waiting, DirectSaleState.Open, DirectSaleState.Closed].includes(directSale.value.state)
    );

    const isBailNeeded = computed(() => userMeta.value?.cantBuyReason === BackendErrorCode.CantPerformUserNoBail);

    const purchaseBlockReasons = computed<BackendErrorCode[]>(() => {
        if (!userMeta.value || userMeta.value.canBuy) return [];

        return getPurchaseBlockReasons({ purchaseBlockReasons: userMeta.value.purchaseBlockReasons, cantReason: userMeta.value.cantBuyReason });
    });

    const hasPurchaseBlock = computed(() => purchaseBlockReasons.value.length > 0);

    const hasRestrictedNotice = computed(
        () =>
            !!userMeta.value &&
            !userMeta.value?.canBuy &&
            !!userMeta.value?.cantBuyReason &&
            ![BackendErrorCode.CantBuyDirectSaleNotOpen, BackendErrorCode.CantBuyDirectSaleHasDirectSaleOrder].includes(
                userMeta.value?.cantBuyReason
            ) &&
            !isBailNeeded.value &&
            !hasPurchaseBlock.value
    );

    const hasInspectionNotice = computed(() => !!userMeta.value?.inspectionExpiredExportRestrictions && !hasPurchaseBlock.value);

    const inspectionReminder = computed<InspectionExpiryStatus | undefined>(() => {
        if (!isSeller.value) return;

        if (!directSale.value.mainAsset.inspectionExpiryDate) return;

        const now = dayjs();
        const target = dayjs(directSale.value.mainAsset.inspectionExpiryDate);

        if (target.isBefore(now)) return 'expired';
        if (target.isBefore(now.add(30, 'day'))) return 'expiringSoon';
    });

    return {
        statusSectionColor,
        title,
        titleColor,
        titleAnnotation,
        titleAnnotationColor,
        priceColor,
        statusDotColor,
        isFeeNoticeHidden,
        hasRestrictedNotice,
        isBailNeeded,
        hasInspectionNotice,
        hasPurchaseBlock,
        purchaseBlockReasons,
        hasOrder,
        isSold,
        isSeller,
        inspectionReminder,
    };
}
